import { faRightLong } from '@fortawesome/pro-solid-svg-icons';
import { FormattedDate, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import defaultShield from '#src/assets/club/shield_none.webp';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '#src/atoms/layout/flex/index.tsx';
import { BodyM, LabelS } from '#src/atoms/typography/index.tsx';
import { PlayerAvatar } from '#src/components/seo/PlayerAvatar/index.tsx';
import { anyPositionNames } from '#src/lib/players.ts';

const LogoContainer = styled.div`
  width: var(--quadruple-unit);
  text-align: center;
`;
const Logo = styled.img`
  height: var(--triple-unit);
`;

export enum TransferDirection {
  IN = 'IN',
  OUT = 'OUT',
}

export type Props = {
  club: {
    slug: string;
    displayName: string;
    pictureUrl: string | null;
    code: string | null;
  };
  transfer: {
    id: string;
    direction: TransferDirection;
    player: {
      displayName: string;
      pictureUrl: string | null;
      position: string;
      to: string;
    };
    date?: Date | null;
    club?: {
      slug: string;
      displayName: string;
      pictureUrl: string | null;
      code: string | null;
      to: string;
    };
  };
};

const ClubLogo = ({ club }: { club?: Props['club'] & { to?: string } }) => {
  if (!club) {
    return <LogoContainer />;
  }

  const logo = (
    <LogoContainer>
      {club.pictureUrl || !club.code ? (
        <Logo
          src={club.pictureUrl || defaultShield}
          alt={club.displayName}
          title={club?.displayName}
        />
      ) : (
        <span>{club.code}</span>
      )}
    </LogoContainer>
  );

  if (!club.to) {
    return logo;
  }

  return <Link to={club.to}>{logo}</Link>;
};

export const Transfer = ({ club, transfer }: Props) => {
  const { formatMessage } = useIntl();
  const fromClub =
    transfer.direction === TransferDirection.OUT ? club : transfer.club;
  const toClub =
    transfer.direction === TransferDirection.OUT ? transfer.club : club;
  const positionName =
    anyPositionNames[transfer.player.position as keyof typeof anyPositionNames];

  return (
    <SBHorizontal>
      <Horizontal>
        <Link to={transfer.player.to}>
          <PlayerAvatar
            src={transfer.player.pictureUrl}
            alt={transfer.player.displayName}
          />
        </Link>
        <Vertical gap={0}>
          {!!transfer.date && (
            <LabelS as="p" color="var(--c-nd-600)">
              <FormattedDate
                value={transfer.date}
                day="2-digit"
                month="2-digit"
                year="numeric"
              />
            </LabelS>
          )}
          <BodyM as="p">
            <Link to={transfer.player.to}>{transfer.player.displayName}</Link>
          </BodyM>
          <LabelS as="p" color="var(--c-nd-600)">
            {positionName
              ? formatMessage(positionName)
              : transfer.player.position}
          </LabelS>
        </Vertical>
      </Horizontal>
      <Horizontal>
        <ClubLogo club={fromClub} />
        <FontAwesomeIcon
          icon={faRightLong}
          color={
            transfer.direction === TransferDirection.OUT
              ? 'var(--c-red-400)'
              : 'var(--c-onBase-success)'
          }
        />
        <ClubLogo club={toClub} />
      </Horizontal>
    </SBHorizontal>
  );
};
